import { useState } from 'react'
import { useForm, Controller } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { toast } from 'sonner'
import { authApi } from '@/api/auth'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
	Form,
	FormControl,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
} from '@/components/ui/form'
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from '@/components/ui/dialog'
import { Separator } from '@/components/ui/separator'
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from '@/components/ui/select'
import { DepartmentSelectField } from '@/components/departments/DepartmentSelectField'
import { ImageUploadField } from '@/components/ui/image-upload'
import {
	AtSign,
	Contact,
	CreditCard,
	Eye,
	EyeOff,
	IdCard,
	Loader2,
	Lock,
	MapPin,
	MessageCircle,
	MessageSquare,
	Phone,
	UserRound,
} from 'lucide-react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { showError } from '@/lib/error'

const phoneRegex = /^1[3-9]\d{9}$/
const idCardRegex = /(^\d{15}$)|(^\d{17}[\dXx]$)/

const optionalEmailSchema = z.string().email('请输入有效的邮箱地址').optional().or(z.literal(''))
const optionalPhoneSchema = z.string().regex(phoneRegex, '请输入有效的手机号').optional().or(z.literal(''))
const optionalIdCardSchema = z.string().regex(idCardRegex, '请输入有效的身份证号').optional().or(z.literal(''))

const createUserSchema = z.object({
	username: z.string().min(2, '用户名至少2个字符').max(64, '用户名不能超过64个字符'),
	email: optionalEmailSchema,
	phone: z.string().min(1, '请输入手机号').regex(phoneRegex, '请输入有效的手机号'),
	password: z.string().min(6, '密码至少6个字符').max(32, '密码不能超过32个字符'),
	confirmPassword: z.string(),
	nickname: z.string().optional().or(z.literal('')),
	department_id: z.number().nullable().optional(),
	wechat: z.string().optional().or(z.literal('')),
	gender: z.number().optional(),
	card_no: optionalIdCardSchema,
	card_front: z.string().optional().or(z.literal('')),
	card_back: z.string().optional().or(z.literal('')),
	emergency_contact: z.string().optional().or(z.literal('')),
	emergency_mobile: optionalPhoneSchema,
	address: z.string().optional().or(z.literal('')),
	remark: z.string().optional().or(z.literal('')),
	self_discipline: z.string().optional().or(z.literal('')),
}).refine((data) => data.password === data.confirmPassword, {
	message: '两次输入的密码不一致',
	path: ['confirmPassword'],
})

type CreateUserData = z.infer<typeof createUserSchema>

interface CreateUserFormProps {
	open: boolean
	onOpenChange: (open: boolean) => void
	onSuccess?: () => void
}

export function CreateUserForm({ open, onOpenChange, onSuccess }: CreateUserFormProps) {
	const [showPassword, setShowPassword] = useState(false)
	const [showConfirmPassword, setShowConfirmPassword] = useState(false)
	const queryClient = useQueryClient()

	const createMutation = useMutation({
		mutationFn: (data: CreateUserData) => authApi.register({
			username: data.username,
			email: data.email || undefined,
			password: data.password,
			nickname: data.nickname || undefined,
			phone: data.phone,
			department_id: data.department_id ?? undefined,
			wechat: data.wechat || undefined,
			gender: data.gender,
			card_no: data.card_no || undefined,
			card_front: data.card_front || undefined,
			card_back: data.card_back || undefined,
			emergency_contact: data.emergency_contact || undefined,
			emergency_mobile: data.emergency_mobile || undefined,
			address: data.address || undefined,
			remark: data.remark || undefined,
			self_discipline: data.self_discipline || undefined,
		}),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ['users'] })
		},
	})

	const form = useForm<CreateUserData>({
		resolver: zodResolver(createUserSchema),
		defaultValues: {
			username: '',
			email: '',
			phone: '',
			nickname: '',
			password: '',
			confirmPassword: '',
			department_id: null,
			wechat: '',
			gender: 0,
			card_no: '',
			card_front: '',
			card_back: '',
			emergency_contact: '',
			emergency_mobile: '',
			address: '',
			remark: '',
			self_discipline: '',
		},
	})

	const onSubmit = async (data: CreateUserData) => {
		try {
			await createMutation.mutateAsync(data)
			toast.success('用户创建成功')
			form.reset()
			onOpenChange(false)
			onSuccess?.()
		} catch (error) {
			console.error('创建用户失败:', error)
			showError(error, '创建用户失败')
		}
	}

	const handleClose = () => {
		form.reset()
		onOpenChange(false)
	}

	return (
		<Dialog open={open} onOpenChange={handleClose}>
			<DialogContent className="flex max-h-[90vh] w-[calc(100vw-2rem)] max-w-5xl flex-col gap-0 overflow-hidden p-0 sm:max-w-5xl">
				<DialogHeader className="shrink-0 border-b px-6 py-5">
					<DialogTitle>新增用户</DialogTitle>
					<DialogDescription>填写以下信息创建新的用户账户</DialogDescription>
				</DialogHeader>

				<Form {...form}>
					<form onSubmit={form.handleSubmit(onSubmit)} className="flex min-h-0 flex-1 flex-col">
						<div className="min-h-0 flex-1 space-y-4 overflow-y-auto px-6 py-5">
							<div className="space-y-4">
								<h4 className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
									<UserRound className="h-4 w-4" />
									基本信息
								</h4>
								<div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
									<FormField control={form.control} name="username" render={({ field }) => (
										<FormItem>
											<FormLabel>用户名 <span className="text-destructive">*</span></FormLabel>
											<FormControl>
												<div className="relative">
													<UserRound className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
													<Input placeholder="2-64个字符" className="pl-9" {...field} />
												</div>
											</FormControl>
											<FormMessage />
										</FormItem>
									)} />
									<FormField control={form.control} name="nickname" render={({ field }) => (
										<FormItem>
											<FormLabel>昵称</FormLabel>
											<FormControl>
												<div className="relative">
													<Contact className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
													<Input placeholder="请输入昵称" className="pl-9" {...field} />
												</div>
											</FormControl>
											<FormMessage />
										</FormItem>
									)} />
									<FormField control={form.control} name="email" render={({ field }) => (
										<FormItem>
											<FormLabel>邮箱</FormLabel>
											<FormControl>
												<div className="relative">
													<AtSign className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
													<Input placeholder="example@email.com" className="pl-9" {...field} />
												</div>
											</FormControl>
											<FormMessage />
										</FormItem>
									)} />
									<FormField control={form.control} name="phone" render={({ field }) => (
										<FormItem>
											<FormLabel>手机号 <span className="text-destructive">*</span></FormLabel>
											<FormControl>
												<div className="relative">
													<Phone className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
													<Input placeholder="13800138000" className="pl-9" {...field} />
												</div>
											</FormControl>
											<FormMessage />
										</FormItem>
									)} />
									<Controller control={form.control} name="department_id" render={({ field }) => (
										<FormItem>
											<FormLabel>部门</FormLabel>
											<FormControl><DepartmentSelectField value={field.value ?? null} onChange={field.onChange} /></FormControl>
											<FormMessage />
										</FormItem>
									)} />
								</div>
							</div>

							<Separator />

							<div className="space-y-4">
								<h4 className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
									<IdCard className="h-4 w-4" />
									个人信息
								</h4>
								<div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
									<FormField control={form.control} name="wechat" render={({ field }) => (
										<FormItem>
											<FormLabel>微信号</FormLabel>
											<FormControl>
												<div className="relative">
													<MessageCircle className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
													<Input placeholder="请输入微信号" className="pl-9" {...field} />
												</div>
											</FormControl>
											<FormMessage />
										</FormItem>
									)} />
									<FormField control={form.control} name="gender" render={({ field }) => (
										<FormItem>
											<FormLabel>性别</FormLabel>
											<Select value={String(field.value ?? 0)} onValueChange={(val) => field.onChange(Number(val))}>
												<FormControl><SelectTrigger><SelectValue placeholder="请选择" /></SelectTrigger></FormControl>
												<SelectContent>
													<SelectItem value="0">未知</SelectItem>
													<SelectItem value="1">男</SelectItem>
													<SelectItem value="2">女</SelectItem>
												</SelectContent>
											</Select>
											<FormMessage />
										</FormItem>
									)} />
								</div>

								<FormField control={form.control} name="card_no" render={({ field }) => (
									<FormItem>
										<FormLabel>身份证号</FormLabel>
										<FormControl>
											<div className="relative">
												<CreditCard className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
												<Input placeholder="请输入身份证号" className="pl-9" {...field} />
											</div>
										</FormControl>
										<FormMessage />
									</FormItem>
								)} />

								<div className="grid grid-cols-1 gap-4 md:grid-cols-3">
									<FormField control={form.control} name="card_front" render={({ field }) => (
										<FormItem>
											<FormLabel>身份证正面照</FormLabel>
											<FormControl><ImageUploadField value={field.value || ''} onChange={field.onChange} placeholder="正面照" /></FormControl>
											<FormMessage />
										</FormItem>
									)} />
									<FormField control={form.control} name="card_back" render={({ field }) => (
										<FormItem>
											<FormLabel>身份证背面照</FormLabel>
											<FormControl><ImageUploadField value={field.value || ''} onChange={field.onChange} placeholder="背面照" /></FormControl>
											<FormMessage />
										</FormItem>
									)} />
									<FormField control={form.control} name="self_discipline" render={({ field }) => (
										<FormItem>
											<FormLabel>业务自律书</FormLabel>
											<FormControl><ImageUploadField value={field.value || ''} onChange={field.onChange} placeholder="自律书" /></FormControl>
											<FormMessage />
										</FormItem>
									)} />
								</div>

								<div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
									<FormField control={form.control} name="emergency_contact" render={({ field }) => (
										<FormItem>
											<FormLabel>紧急联系人</FormLabel>
											<FormControl>
												<div className="relative">
													<Contact className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
													<Input placeholder="联系人姓名" className="pl-9" {...field} />
												</div>
											</FormControl>
											<FormMessage />
										</FormItem>
									)} />
									<FormField control={form.control} name="emergency_mobile" render={({ field }) => (
										<FormItem>
											<FormLabel>紧急联系人电话</FormLabel>
											<FormControl>
												<div className="relative">
													<Phone className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
													<Input placeholder="紧急联系人电话" className="pl-9" {...field} />
												</div>
											</FormControl>
											<FormMessage />
										</FormItem>
									)} />
								</div>

								<FormField control={form.control} name="address" render={({ field }) => (
									<FormItem>
										<FormLabel>地址</FormLabel>
										<FormControl>
											<div className="relative">
												<MapPin className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
												<Input placeholder="请输入地址" className="pl-9" {...field} />
											</div>
										</FormControl>
										<FormMessage />
									</FormItem>
								)} />

								<FormField control={form.control} name="remark" render={({ field }) => (
									<FormItem>
										<FormLabel>备注</FormLabel>
										<FormControl>
											<div className="relative">
												<MessageSquare className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
												<Input placeholder="备注信息" className="pl-9" {...field} />
											</div>
										</FormControl>
										<FormMessage />
									</FormItem>
								)} />
							</div>

							<Separator />

							<div className="space-y-4">
								<h4 className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
									<Lock className="h-4 w-4" />
									密码设置
								</h4>
								<div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
									<FormField control={form.control} name="password" render={({ field }) => (
										<FormItem>
											<FormLabel>密码 <span className="text-destructive">*</span></FormLabel>
											<FormControl>
												<div className="relative">
													<Lock className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
													<Input type={showPassword ? "text" : "password"} placeholder="6-32个字符" className="px-9" {...field} />
													<Button type="button" variant="ghost" size="icon" className="absolute right-1 top-1/2 h-7 w-7 -translate-y-1/2" onClick={() => setShowPassword(!showPassword)}>
														{showPassword ? <EyeOff className="h-4 w-4 text-muted-foreground" /> : <Eye className="h-4 w-4 text-muted-foreground" />}
													</Button>
												</div>
											</FormControl>
											<FormMessage />
										</FormItem>
									)} />
									<FormField control={form.control} name="confirmPassword" render={({ field }) => (
										<FormItem>
											<FormLabel>确认密码 <span className="text-destructive">*</span></FormLabel>
											<FormControl>
												<div className="relative">
													<Lock className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
													<Input type={showConfirmPassword ? "text" : "password"} placeholder="请再次输入密码" className="px-9" {...field} />
													<Button type="button" variant="ghost" size="icon" className="absolute right-1 top-1/2 h-7 w-7 -translate-y-1/2" onClick={() => setShowConfirmPassword(!showConfirmPassword)}>
														{showConfirmPassword ? <EyeOff className="h-4 w-4 text-muted-foreground" /> : <Eye className="h-4 w-4 text-muted-foreground" />}
													</Button>
												</div>
											</FormControl>
											<FormMessage />
										</FormItem>
									)} />
								</div>
							</div>
						</div>

						<DialogFooter className="shrink-0 gap-2 border-t px-6 py-4 sm:gap-0">
							<Button type="button" variant="outline" onClick={handleClose}>取消</Button>
							<Button type="submit" disabled={createMutation.isPending}>
								{createMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
								创建用户
							</Button>
						</DialogFooter>
					</form>
				</Form>
			</DialogContent>
		</Dialog>
	)
}
